/*
 * Contact Queries And Mutations
 *
 * Features:
 * - Shares team, current-user, and detail contact reads
 * - Keeps contact lists, details, My-list membership, and delete cleanup coherent
 *
 * Implementation:
 * - All network calls continue through dataService
 * - Mutation identifiers are normalized or rejected before request execution
 * - Delete cleanup removes the active detail before list invalidation and router navigation
 *
 * Changes:
 * - 2026-08-06: Added the contact half of the TanStack Query server-cache pilot
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import type { Contact } from '../models/Contact';
import { request } from '../services/dataService';
import { contactKeys, normalizePositiveId, normalizeTeamId, requirePositiveId } from './queryKeys';

interface CreateContactPayload {
  name: string;
  data: string;
  accountId: number;
}

interface UpdateContactPayload {
  id: number;
  name?: string;
  data?: string;
}

interface LinkContactPayload {
  personId: number;
}

interface ContactIdentityPayload {
  id: number;
}

interface UserContactPayload extends ContactIdentityPayload {
  userId: number;
}

export function useTeamContactsQuery(teamId: string | number | null | undefined) {
  const normalizedTeamId = normalizeTeamId(teamId);
  return useQuery<Contact[]>({
    queryKey: contactKeys.team(normalizedTeamId),
    queryFn: () => request(API_ENDPOINTS.GET_TEAM_CONTACTS, { teamId: normalizedTeamId }),
    enabled: normalizedTeamId !== null,
  });
}

export function useUserContactsQuery(userId: string | number | null | undefined) {
  const normalizedUserId = normalizePositiveId(userId);
  return useQuery<Contact[]>({
    queryKey: contactKeys.user(normalizedUserId),
    queryFn: () => request(API_ENDPOINTS.GET_USER_CONTACTS, { userId: normalizedUserId }),
    enabled: normalizedUserId !== null,
  });
}

export function useContactQuery(id: string | number | null | undefined) {
  const normalizedId = normalizePositiveId(id);
  return useQuery<Contact | null>({
    queryKey: contactKeys.detail(normalizedId),
    queryFn: () => request(API_ENDPOINTS.GET_CONTACT, { id: normalizedId }),
    enabled: normalizedId !== null,
  });
}

export function useCreateContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContactPayload) => request(API_ENDPOINTS.POST_CONTACT, {
      ...payload,
      accountId: requirePositiveId(payload.accountId, 'accountId'),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactKeys.lists() }),
  });
}

export function useLinkContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LinkContactPayload) => request(API_ENDPOINTS.PUT_CONTACT, {
      personId: requirePositiveId(payload.personId, 'personId'),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactKeys.lists() }),
  });
}

export function useUpdateContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateContactPayload) => request(API_ENDPOINTS.PATCH_CONTACT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
    }),
    onSuccess: async (_data, payload) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contactKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: contactKeys.detail(payload.id), exact: true }),
      ]);
    },
  });
}

export function useDeleteContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: ContactIdentityPayload) => request(API_ENDPOINTS.DELETE_CONTACT, {
      id: requirePositiveId(id, 'id'),
    }),
    onSuccess: async (_data, { id }) => {
      await queryClient.cancelQueries({ queryKey: contactKeys.detail(id), exact: true });
      queryClient.removeQueries({ queryKey: contactKeys.detail(id), exact: true });
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
    },
  });
}

export function useAddUserContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserContactPayload) => request(API_ENDPOINTS.POST_USER_CONTACT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
      userId: requirePositiveId(payload.userId, 'userId'),
    }),
    onSuccess: (_data, { userId }) => queryClient.invalidateQueries({
      queryKey: contactKeys.user(userId),
      exact: true,
    }),
  });
}

export function useRemoveUserContactMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserContactPayload) => request(API_ENDPOINTS.DELETE_USER_CONTACT, {
      ...payload,
      id: requirePositiveId(payload.id, 'id'),
      userId: requirePositiveId(payload.userId, 'userId'),
    }),
    onSuccess: (_data, { userId }) => queryClient.invalidateQueries({
      queryKey: contactKeys.user(userId),
      exact: true,
    }),
  });
}
